// useChatMessages (issue #419) — cursor-paginated message stream.
//
// Reads `GET /chats/:chatId/messages?cursor=&limit=` via `useInfiniteQuery`.
// Cursor semantics: each page returns `meta.nextCursor` which is the lowest
// message id in that page; the next call passes it as `cursor` to fetch the
// strictly older window.

import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
} from '@tanstack/react-query';

import { getChatMessages, SupportInboxScope } from './api';
import { chatKeys } from './keys';

import type { ChatMessagesPage } from 'src/models/chat';

const DEFAULT_LIMIT = 50;

export function useChatMessages(
  chatId: number | undefined,
  options?: Omit<
    UseInfiniteQueryOptions<ChatMessagesPage>,
    'queryKey' | 'queryFn' | 'getNextPageParam'
  >,
  scope: SupportInboxScope = 'legacy'
) {
  return useInfiniteQuery<ChatMessagesPage>(
    chatKeys.chatMessages(chatId ?? 0),
    ({ pageParam }) =>
      getChatMessages(chatId as number, {
        cursor: pageParam as number | undefined,
        limit: DEFAULT_LIMIT,
      }, scope),
    {
      enabled: typeof chatId === 'number' && chatId > 0,
      getNextPageParam: (lastPage) => lastPage?.meta?.nextCursor ?? undefined,
      ...options,
    }
  );
}
